import { createSlice } from '@reduxjs/toolkit';

import { LIQUID_ID_FACES_LENGTH } from '@app/pages/Prototype';
import { ContentType } from '@app/store/types/yat-page-actions';

import {
    type AddBulkItemsAction,
    type AddItemAction,
    type AddLiquidIDItemAction,
    LiquidIDEditStage,
    PublishState,
    type RemoveBulkItemsAction,
    type RemoveItemAction,
    type ReorderLiquidIDAction,
    type ReplaceItemTempIdAction,
    type SetBioAction,
    type SetDraggingAction,
    type SetEditingItemAction,
    type SetHeadingAction,
    type SetIsLoadingItems,
    type SetIsOwnerAction,
    type SetItemsAction,
    type SetLiquidIDAction,
    type SetLiquidIdEditStage,
    type SetLiquidIDExpandedPreviewAction,
    type setLiquidIDFaceSelectionAction,
    type SetLiquidIdItemsPersistedAction,
    type SetLiquidIDLoadingAction,
    type SetLiquidIDVisibleFaceAction,
    type SetPageAction,
    type SetPagingAction,
    type SetPreviewItemAction,
    type SetPublicPageAction,
    type SetVersionAction,
    type StageLiquidIDItemAction,
    type UpdateEditStatesAction,
    type UpdateItemAction,
    type UpdateItemsOrderAction,
    type YatPageEditStates,
    type YatPageState,
} from './types';
import { yatPageEditingAnything } from './utils';

export const TITLE_MAX_LENGTH = 50;
export const DESCRIPTION_MAX_LENGTH = 160;

const name = 'yatPage';

const defaultEditStates: YatPageEditStates = {
    editingHeading: false,
    editingBio: false,
    editingSocial: false,
    editingItem: false,
    editingLiquidID: false,
};

const emptyState: Omit<YatPageState, 'publishedState' | 'yatPageVersion'> = {
    id: null,
    eid: null,
    featured_id: null,
    bio: null,
    heading: null,
    draft_bio: null,
    draft_heading: null,
    isLoadingItems: true,
    liquidIDItems: Array(LIQUID_ID_FACES_LENGTH).fill(undefined),
    liquidIDLoading: false,
    liquidIDFaceSelection: undefined,
    liquidIDEditStage: LiquidIDEditStage.Empty,
    liquidIDSelectedItem: undefined,
    liquidIDExpandedPreview: false,
    liquid_id: null,
    liquidIDVisibleFace: 0,
    user_id: null,
    pageItemsPersisted: {
        pageItems: [],
        liquidIdItems: Array(LIQUID_ID_FACES_LENGTH).fill(undefined),
    },
    editStates: defaultEditStates,
    pageAssets: {
        draft: {},
        published: {},
        modified: [],
        sorted: [],
        toggled: {},
    },
    autoProfileSetting: false,
    replacedIds: [],
};

const initialState: YatPageState = {
    ...emptyState,
    isPageOwner: false,
    publishedState: PublishState.Unchanged,
    yatPageVersion: null,
};

export const yatPageSlice = createSlice({
    name,
    initialState,
    reducers: {
        resetYatPageStore() {
            return initialState;
        },
        setYatPage(state, { payload }: SetPageAction) {
            return { ...state, ...payload };
        },
        // TODO: This isn't being used anywhere.
        setPublicYatPage(state, { payload }: SetPublicPageAction) {
            return { ...state, ...payload };
        },

        setIsLoadingItems(state, { payload }: SetIsLoadingItems) {
            state.isLoadingItems = payload;
        },
        setEmptyYatPage(state) {
            return { ...state, ...emptyState };
        },
        setYatPageAssets(state, { payload }: SetItemsAction) {
            const draft = {};
            const published = {};
            const sorted: string[] = [];

            let indexMismatch = false;

            for (let i = 0; i < payload.length; i++) {
                const asset = payload[i];

                draft[asset.id] = {
                    asset,
                    index: i,
                };
                published[asset.id] = {
                    asset,
                    index: asset.order,
                };
                sorted[i] = asset.id;

                if (i !== asset.draftOrder) {
                    indexMismatch = true;
                }
            }

            if (indexMismatch) {
                state.pageAssets.modified = sorted;
            }

            state.pageAssets.draft = draft;
            state.pageAssets.published = published;
            state.pageAssets.sorted = sorted;
        },
        setYatPageLiquidIdItemsPersisted(state, { payload }: SetLiquidIdItemsPersistedAction) {
            state.pageItemsPersisted = { ...state.pageItemsPersisted, liquidIdItems: payload };
        },
        setYatPagePaging(state, { payload }: SetPagingAction) {
            state.paging = payload;
        },
        setYatPageVersion(state, { payload }: SetVersionAction) {
            state.yatPageVersion = payload;
        },
        setIsYatPageOwner(state, { payload }: SetIsOwnerAction) {
            state.isPageOwner = payload;
        },
        // TODO: These should be named `title` and `description`.
        setYatPageHeading(state, { payload }: SetHeadingAction) {
            state.heading = payload;
        },
        setYatPageBio(state, { payload }: SetBioAction) {
            state.bio = payload;
        },
        setLiquidIdLoading(state, { payload }: SetLiquidIDLoadingAction) {
            state.liquidIDLoading = payload;
        },
        setDraggingLiquidIDThumbnail(state, { payload }: SetDraggingAction) {
            state.draggingLiquidIDThumbnail = payload;
        },

        setYatPageLiquidID(state, { payload }: SetLiquidIDAction) {
            let items = payload.items;
            if (items.length < LIQUID_ID_FACES_LENGTH) {
                items = [...items, ...Array(LIQUID_ID_FACES_LENGTH - items.length).fill(undefined)];
            }
            state.liquidIDItems = items;
            const nextIndex = items.findIndex((a) => a === undefined);

            if (!payload.unchanged || !payload.items.every((item) => item?.order === item?.draftOrder)) {
                state.publishedState = PublishState.Modified;
            }
            state.liquidIDFaceSelection = nextIndex;
        },

        setLiquidIdEditStage(state, { payload }: SetLiquidIdEditStage) {
            if (payload === LiquidIDEditStage.Added && state.liquidIDItems.length < 1) {
                state.liquidIDEditStage = LiquidIDEditStage.Empty;
            } else {
                state.liquidIDEditStage = payload;
            }
        },
        addLiquidIDItem(state, { payload }: AddLiquidIDItemAction) {
            const items = [...state.liquidIDItems];
            const index = payload.face || items.findIndex((a) => a === undefined);

            items[index] = payload.item;

            const nextIndex = items.findIndex((a) => a === undefined);

            return {
                ...state,
                liquidIDItems: items,
                publishedState: PublishState.Modified,
                liquidIDFaceSelection: nextIndex,
            };
        },
        stageLiquidIDItem(state, { payload }: StageLiquidIDItemAction) {
            const updatedLiquidIDSelectedItem = payload?.id === state.liquidIDSelectedItem?.id ? undefined : payload;
            return { ...state, liquidIDSelectedItem: updatedLiquidIDSelectedItem };
        },
        addStagedLiquidIDItem(state) {
            if (!state.liquidIDSelectedItem) return state;

            const item = { ...state.liquidIDSelectedItem };
            const items = [...state.liquidIDItems];
            const index = state.liquidIDFaceSelection || items.findIndex((a) => a === undefined);

            items[index] = item;

            const nextIndex = items.findIndex((a) => a === undefined);

            return {
                ...state,
                liquidIDItems: items,
                liquidIDSelectedItem: undefined,
                liquidIDFaceSelection: nextIndex,
            };
        },
        reorderLiquidID(state, { payload }: ReorderLiquidIDAction) {
            const updatedOrder = [...state.liquidIDItems];
            const draggingItemIndex = payload.from;
            const targetItemIndex = payload.to;
            const itemToSwap = updatedOrder[draggingItemIndex];
            updatedOrder[draggingItemIndex] = updatedOrder[targetItemIndex];
            updatedOrder[targetItemIndex] = itemToSwap;
            const nextIndex = updatedOrder.findIndex((a) => a === undefined);

            return {
                ...state,
                publishedState: PublishState.Modified,
                liquidIDItems: updatedOrder,
                liquidIDFaceSelection: nextIndex,
            };
        },
        setLiquidIDExpandedPreview(state, { payload }: SetLiquidIDExpandedPreviewAction) {
            state.liquidIDExpandedPreview = payload;
        },
        setLiquidIDFaceSelection(state, { payload }: setLiquidIDFaceSelectionAction) {
            state.liquidIDFaceSelection = payload;
        },
        setLiquidIDVisibleFace(state, { payload }: SetLiquidIDVisibleFaceAction) {
            state.liquidIDVisibleFace = payload;
        },

        setYatPagePreviewItem(state, { payload }: SetPreviewItemAction) {
            state.previewItem = payload;
        },

        // NOTE: The remaining functions where all async,
        // but I am unsure if this is actually required?

        setYatPageDragging(state, { payload }: SetDraggingAction) {
            state.draggingId = payload;
        },
        setYatPageEditingItem(state, { payload }: SetEditingItemAction) {
            state.editingItemId = payload;
        },

        updateYatPageEditStates(state, { payload }: UpdateEditStatesAction) {
            const updatedEditStates = {
                ...state.editStates,
                ...payload,
            };

            const editingAnything = yatPageEditingAnything(updatedEditStates);

            state.editStates = updatedEditStates;
            state.hideAddItemUi = editingAnything;
        },

        addYatPageItem(state, { payload }: AddItemAction) {
            const draftAssets = { ...state.pageAssets.draft };
            const sortedPageAssets = [...state.pageAssets.sorted];
            const modifiedAssets = new Set(sortedPageAssets); // All assets will be moved

            // Asset already added to page
            if (payload.id in draftAssets) return state;

            // Move all existing assets one position
            const movedDraftAssets = Object.fromEntries(
                Object.entries(draftAssets).map(([k, v]) => [k, { ...v, index: v.index + 1 }]),
            );

            // Add item from sorted list and dictionary
            sortedPageAssets.unshift(payload.id);
            movedDraftAssets[payload.id] = {
                asset: payload,
                index: 0,
            };

            if (payload.type !== ContentType.LINK || (payload.type !== ContentType.LINK && !payload.isURLInvalid)) {
                modifiedAssets.add(payload.id);
            }

            let invalidLink: string | undefined = undefined;
            if (payload.type === ContentType.LINK) {
                invalidLink = payload.id;
            }

            const toggled = { ...state.pageAssets.toggled };
            toggled[payload.id] = 'added';

            // Update page assets
            const pageAssets = {
                ...state.pageAssets,
                draft: movedDraftAssets,
                sorted: sortedPageAssets,
                modified: Array.from(modifiedAssets),
                toggled,
            };

            return { ...state, pageAssets, publishedState: PublishState.Modified, invalidLink };
        },

        removeInvalidLink(state) {
            state.invalidLink = undefined;
        },

        replaceAssetTempId(state, { payload }: ReplaceItemTempIdAction) {
            const draftAssets = { ...state.pageAssets.draft };
            const sortedPageAssets = [...state.pageAssets.sorted];

            // Update object
            draftAssets[payload.tempId] = {
                ...draftAssets[payload.id],
                asset: {
                    ...draftAssets[payload.tempId].asset,
                    id: payload.id,
                    hasPersisted: true,
                },
                index: 0,
            };
            draftAssets[payload.id] = draftAssets[payload.tempId];

            // Update page assets
            const pageAssets = {
                ...state.pageAssets,
                draft: draftAssets,
                sorted: sortedPageAssets,
            };

            return {
                ...state,
                pageAssets,
                replacedIds: [...state.replacedIds, { newId: payload.id, tempId: payload.tempId }],
            };
        },

        addYatPageBulkItems(state, { payload }: AddBulkItemsAction) {
            const toggled = { ...state.pageAssets.toggled };

            const newAssetsIds = payload.map((item) => {
                // Take advantage of map to update toggled items
                toggled[item.id] = 'added';
                return item.id;
            });

            const newSortedPageAssets = [...newAssetsIds, ...state.pageAssets.sorted];
            const currentDraftAssets = { ...state.pageAssets.draft };
            const newDraftAssets = {};

            // Add new assets (pull assets from payload)
            for (const newAsset of payload) {
                const newAssetId = newAsset.id;
                newDraftAssets[newAssetId] = {
                    asset: newAsset,
                    index: newSortedPageAssets.indexOf(newAssetId),
                };
            }

            // Add original contents with updated index
            for (const assetId of state.pageAssets.sorted) {
                newDraftAssets[assetId] = {
                    asset: currentDraftAssets[assetId].asset,
                    index: newSortedPageAssets.indexOf(assetId),
                };
            }

            const pageAssets = {
                ...state.pageAssets,
                draft: newDraftAssets,
                sorted: newSortedPageAssets,
                modified: newSortedPageAssets,
            };

            return { ...state, pageAssets, publishedState: PublishState.Modified };
        },

        updateYatPageItem(state, { payload }: UpdateItemAction) {
            const sortedPageAssets = [...state.pageAssets.sorted];
            const draftAssets = { ...state.pageAssets.draft };
            const persistedAssetId = payload.id;
            const assetIndex = draftAssets[persistedAssetId].index;
            const tempAssetId = sortedPageAssets[assetIndex];

            /*
             * The purpose of this line is to prevent the grid to refresh when a link card is persisted and a new ID is given to it.
             * Before the first refresh, the temporary id will remain as reference for the asset, both for the sorted array and
             * for the asset dictionary.
             * The actual persisted id is not changed and when a call is made to the API to update the item, the real asset id
             * (the one in the "asset" property) is used
             */
            const itemId = ContentType.LINK && tempAssetId !== persistedAssetId ? tempAssetId : persistedAssetId;

            const cantPublish = draftAssets[itemId].asset.type === ContentType.LINK && payload.isURLInvalid;
            const modifiedAssets = new Set(sortedPageAssets);

            draftAssets[itemId] = {
                ...draftAssets[itemId],
                asset: payload,
            };

            modifiedAssets.add(itemId);

            const pageAssets = {
                ...state.pageAssets,
                draft: draftAssets,
                modified: Array.from(modifiedAssets),
            };

            return { ...state, cantPublish, pageAssets, publishedState: PublishState.Modified };
        },
        updateYatPageItemsOrder(state, { payload: targetAssetId }: UpdateItemsOrderAction) {
            if (!state.draggingId) return state;

            const draftAssets = { ...state.pageAssets.draft };
            const sortedPageAssets = [...state.pageAssets.sorted];
            const originAssetId = state.draggingId;
            const totalItems = sortedPageAssets.length;
            const modifiedAssets = new Set(state.pageAssets.modified);

            const originIndex = draftAssets[originAssetId].index;
            const targetIndex = draftAssets[targetAssetId].index;

            if (originIndex === -1 || targetIndex === -1 || originIndex === targetIndex) return state;

            // Remove asset being dragged from the list
            sortedPageAssets.splice(originIndex, 1);

            // Add asset into target position (use the most efficient approach)
            if (targetIndex === 0) {
                sortedPageAssets.unshift(originAssetId);
            } else if (targetIndex === totalItems - 1) {
                sortedPageAssets.push(originAssetId);
            } else {
                sortedPageAssets.splice(targetIndex, 0, originAssetId);
            }

            // // Save new index reference
            draftAssets[originAssetId] = {
                asset: draftAssets[originAssetId].asset,
                index: targetIndex,
            };
            modifiedAssets.add(draftAssets[originAssetId].asset.id);

            // Update position reference ONLY for assets that were repositioned
            if (targetIndex > originIndex) {
                // Asset was moved to the right
                for (let i = originIndex; i < targetIndex; i++) {
                    const movedAssetId = sortedPageAssets[i];
                    draftAssets[movedAssetId] = {
                        asset: draftAssets[movedAssetId].asset,
                        index: draftAssets[movedAssetId].index - 1,
                    };
                    modifiedAssets.add(draftAssets[movedAssetId].asset.id);
                }
            } else {
                // Asset was moved to the left
                for (let i = targetIndex + 1; i <= originIndex; i++) {
                    const movedAssetId = sortedPageAssets[i];
                    draftAssets[movedAssetId] = {
                        asset: draftAssets[movedAssetId].asset,
                        index: draftAssets[movedAssetId].index + 1,
                    };
                    modifiedAssets.add(draftAssets[movedAssetId].asset.id);
                }
            }

            const pageAssets = {
                ...state.pageAssets,
                draft: draftAssets,
                sorted: sortedPageAssets,
                modified: Array.from(modifiedAssets),
            };

            return { ...state, pageAssets, publishedState: PublishState.Modified };
        },

        removeYatPageItem(state, { payload }: RemoveItemAction) {
            const draftAssets = { ...state.pageAssets.draft };
            const modifiedAssets = new Set(state.pageAssets.modified);
            const sortedPageAssets = [...state.pageAssets.sorted];

            const replacedId = state.replacedIds.find((replacedId) => replacedId.newId === payload)?.tempId;

            // Remove item from sorted list
            const newSortedPageAssets = sortedPageAssets.filter(
                (pageAssetId) => pageAssetId !== payload && pageAssetId !== replacedId,
            );

            if (state.pageAssets.draft[payload]) {
                const deletedIndex = state.pageAssets.draft[payload].index;

                // Decrease position of all assets after deleted item
                for (let i = deletedIndex + 1; i < sortedPageAssets.length; i++) {
                    const movedAssetId = sortedPageAssets[i];
                    if (!draftAssets[movedAssetId]) continue;
                    draftAssets[movedAssetId] = {
                        asset: draftAssets[movedAssetId].asset,
                        index: draftAssets[movedAssetId].index - 1,
                    };
                    modifiedAssets.add(draftAssets[movedAssetId].asset.id);
                }
            }

            // Remove item from dictionary
            delete draftAssets[payload];
            const toggled = { ...state.pageAssets.toggled };
            toggled[payload] = 'removed';

            const pageAssets = {
                ...state.pageAssets,
                draft: draftAssets,
                sorted: newSortedPageAssets,
                modified: Array.from(modifiedAssets),
                toggled,
            };

            let invalidLink = state.invalidLink;
            let cantPublish = state.cantPublish;

            if (invalidLink === payload) {
                invalidLink = undefined;
                cantPublish = false;
            }

            return { ...state, pageAssets, publishedState: PublishState.Modified, invalidLink, cantPublish };
        },
        removeYatPageBulkItems(state, { payload }: RemoveBulkItemsAction) {
            const newSortedPageAssets = state.pageAssets.sorted.filter((assetId) => !payload.includes(assetId));
            const currentDraftAssets = { ...state.pageAssets.draft };
            const newDraftAssets = {};

            const toggled = { ...state.pageAssets.toggled };
            payload.forEach((assetId) => (toggled[assetId] = 'removed'));

            // Recreate draft object
            for (const assetId of newSortedPageAssets) {
                newDraftAssets[assetId] = {
                    ...currentDraftAssets[assetId],
                    index: newSortedPageAssets.indexOf(assetId),
                };
            }

            const pageAssets = {
                ...state.pageAssets,
                draft: newDraftAssets,
                sorted: newSortedPageAssets,
                modified: newSortedPageAssets,
                toggled,
            };

            return { ...state, pageAssets, publishedState: PublishState.Modified };
        },

        // TODO: Should we simply have a single `setYatPagePublishState()` action?

        publishYatPage(state) {
            state.publishedState = PublishState.Published;
        },
        updateExternalLayout(state) {
            state.publishedState = PublishState.Modified;
        },
        removeModifiedStatus(state) {
            state.publishedState = PublishState.Unchanged;
        },
        addYatPageModifiedItem(state, { payload }: RemoveItemAction) {
            const newtModifiedItems = new Set(state.pageAssets.modified);
            newtModifiedItems.add(payload);
            state.pageAssets.modified = Array.from(newtModifiedItems);
        },

        removeYatPageModifiedItem(state, { payload }: RemoveItemAction) {
            const newtModifiedItems = new Set(state.pageAssets.modified);
            if (newtModifiedItems.has(payload)) {
                newtModifiedItems.delete(payload);
            }
            state.pageAssets.modified = Array.from(newtModifiedItems);
        },
        updateAutoProfile(state, { payload }) {
            return { ...state, autoProfileSetting: payload };
        },
    },
});
